import 'vite/modulepreload-polyfill';
import { computed, createApp, onMounted, onUnmounted, ref } from 'vue';

import { GEOFENCING_COOKIES } from '~/constants/cookies';
import {
  GEOFENCING_COUNTRY_TO_SHOP_SUPPORT_MAP,
  GEOFENCING_SHOP_IDS,
  GEOFENCING_SHOPS,
} from '~/constants/geofencing';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { getCookie, setCookie } from '~/utils/cookies';

import { BaseSection } from './base-section';
/**
 * GeofencingSection handles country-based shop redirection and selection
 * It displays a modal when users are detected to be from a different country
 * than the current shop's country.
 */
class GeofencingSection extends BaseSection {
  constructor() {
    super('[data-section-type="geofencing"]');
  }

  init(instanceData) {
    const container = instanceData.container;
    const inferredUserCountryCookie = getCookie(GEOFENCING_COOKIES.USER_INFERRED_COUNTRY);
    const userSelectedShopCookie = getCookie(GEOFENCING_COOKIES.USER_SELECTED_SHOP);
    const shopCountry = container.getAttribute('data-shop-country');

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Geofencing - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    // Initialize the app even without the cookie since we'll check Shopify's country
    createApp({
      name: 'GeofencingSection',
      setup() {
        const isGeofencingModalOpen = ref(false);
        const userCountryIso = ref(inferredUserCountryCookie || null);
        const userSelectedShop = ref(userSelectedShopCookie || shopCountry || null);

        // Computed properties
        const availableShops = computed(() => [
          GEOFENCING_SHOPS[GEOFENCING_SHOP_IDS.US],
          ...Object.values(GEOFENCING_SHOPS)
            .filter((shop) => shop.id !== GEOFENCING_SHOP_IDS.US)
            .sort((a, b) => a.name.localeCompare(b.name)),
        ]);

        const isSupportedCountry = computed(() =>
          Boolean(GEOFENCING_COUNTRY_TO_SHOP_SUPPORT_MAP[userCountryIso.value]?.length),
        );

        const supportedShops = computed(
          () => GEOFENCING_COUNTRY_TO_SHOP_SUPPORT_MAP[userCountryIso.value] || [],
        );

        /**
         * Checks geofencing cookie and opens modal if user is from a different country
         */
        const checkGeofencingCookie = () => {
          const firstSupportedShop = supportedShops.value.at(0);
          if (firstSupportedShop && shopCountry === userCountryIso.value) {
            userSelectedShop.value = firstSupportedShop;
          }

          if (userCountryIso.value !== shopCountry) {
            isGeofencingModalOpen.value = true;
          }
        };

        /**
         * Handles modal close action and saves user selection
         */
        const handleModalClose = () => {
          setCookie(GEOFENCING_COOKIES.USER_SELECTED_SHOP, userSelectedShop.value);
          isGeofencingModalOpen.value = false;
        };

        /**
         * Handles escape key press for modal closing
         */
        const handleEscapeKey = (event) => {
          if (event.key === 'Escape' && isGeofencingModalOpen.value) {
            handleModalClose();
          }
        };

        /**
         * Handles shop selection and redirects user if necessary
         */
        const handleShopSelection = (shopId) => {
          const shop = GEOFENCING_SHOPS[GEOFENCING_SHOP_IDS[shopId]];

          if (!shop?.url || !shop.id) {
            DataDogLogs.warn(
              '[Allbirds section] - Geofencing - handleShopSelection - Shop not found or has no URL',
              { shopId },
              new Error('Shop not found or has no URL'),
            );
            return;
          }

          userSelectedShop.value = shop.id;
          setCookie(GEOFENCING_COOKIES.USER_SELECTED_SHOP, shop.id);

          window.location.href = shop.url;
        };

        /**
         * Sets the geofencing cookie using Shopify's consent tracking API
         * and triggers the geofencing check once the region is determined
         */
        const setGeoFencingCookie = () => {
          window.Shopify?.loadFeatures?.(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            function (error) {
              if (error) {
                DataDogLogs.warn(
                  '[Allbirds section] - Geofencing - setGeoFencingCookie - Error setting geofencing cookie',
                  {},
                  error,
                );
                return;
              }

              DataDogLogs.info(
                '[Allbirds section] - Geofencing - setGeoFencingCookie - Successfully loaded shopify consent-tracking-api',
                {},
              );

              if (
                window.Shopify?.customerPrivacy?.getRegion &&
                typeof window.Shopify.customerPrivacy.getRegion === 'function'
              ) {
                const userRegion = window.Shopify.customerPrivacy.getRegion().slice(0, 2);
                if (userRegion && !userCountryIso.value) {
                  setCookie(GEOFENCING_COOKIES.USER_INFERRED_COUNTRY, userRegion);
                  userCountryIso.value = userRegion;
                  checkGeofencingCookie();

                  DataDogLogs.info(
                    '[Allbirds section] - Geofencing - setGeoFencingCookie - Successfully set user region cookie',
                    { userRegion },
                  );
                } else {
                  DataDogLogs.warn(
                    '[Allbirds section] - Geofencing - setGeoFencingCookie - Error User region not found',
                    {},
                    new Error('User region not found'),
                  );
                }
              }
            },
          );
        };

        onMounted(() => {
          // 'geofenceoff' query param is used to disable Geofenncing
          const params = new URLSearchParams(window.location.search);
          const isGeofencingOff = params.get('geofenceoff') === 'true';
          if (isGeofencingOff) {
            DataDogLogs.warn(
              '[Allbirds section] - Geofencing - onMounted - Not loaded, geofenceoff parameter is true',
              {},
              new Error('Not loaded, geofenceoff parameter is true'),
            );
            return;
          }
          if (!userSelectedShopCookie) {
            setGeoFencingCookie();
            // Only check geofencing if we already have the country cookie
            if (userCountryIso.value) {
              checkGeofencingCookie();
            }
          }

          if (isGeofencingModalOpen.value) {
            document.addEventListener('keydown', handleEscapeKey);
          }
        });

        onUnmounted(() => {
          document.removeEventListener('keydown', handleEscapeKey);
        });

        return {
          availableShops,
          handleModalClose,
          handleShopSelection,
          isGeofencingModalOpen,
          isSupportedCountry,
          userCountryIso,
          userSelectedShop,
        };
      },
    }).mount(container);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new GeofencingSection();
});
